import openRouterClient from '../lib/openRouterClient';
import genAI, { getSafetySettings, handleGeminiError } from '../lib/geminiClient';

/**
 * AI Service with OpenRouter integration (primary) and Gemini fallback
 * 
 * Priority:
 * 1. OpenRouter (if API key is configured) - Supports GPT-4, Claude, Gemini, DeepSeek
 * 2. Gemini Direct (fallback) - If OpenRouter is not available
 * 
 * This approach gives us maximum flexibility while maintaining reliability
 */
export class GeminiService {
  /**
   * Check if OpenRouter is configured and should be used
   * @returns {boolean} True if OpenRouter API key exists
   */
  static useOpenRouter() {
    const hasKey = Boolean(import.meta.env.VITE_OPENROUTER_API_KEY);
    if (hasKey) {
      console.log('✅ Using OpenRouter for AI operations');
    } else {
      console.log('⚠️ OpenRouter not configured, using Gemini fallback');
    }
    return hasKey;
  }

  /**
   * Generate text - routes to OpenRouter or Gemini
   * @param {string} prompt - The user's input prompt
   * @param {string} userTier - User tier ('free', 'premium', 'enterprise')
   * @param {AbortSignal} signal - Optional abort signal
   * @returns {Promise<string>} The generated text
   */
  static async generateText(prompt, userTier = 'free', signal = null) {
    try {
      // Route 1: Use OpenRouter (Primary)
      if (this.useOpenRouter()) {
        return await openRouterClient.generateText(prompt, userTier, {
          signal
        });
      }
      
      // Route 2: Fallback to Gemini Direct
      console.log('📡 Using Gemini Direct API');
      const modelId = userTier === 'free' ? 'gemini-2.0-flash-exp' : 'gemini-2.0-flash-thinking-exp-1219';
      const model = genAI.getGenerativeModel({ 
        model: modelId,
        safetySettings: getSafetySettings()
      });

      const result = await model.generateContent({
        contents: [{ role: 'user', parts: [{ text: prompt }] }],
        generationConfig: {
          temperature: 0.7,
          topP: 0.8,
          topK: 40,
          maxOutputTokens: userTier === 'free' ? 1024 : 2048
        }
      });
      
      const response = await result.response;
      return response.text();
    } catch (error) {
      console.error('❌ AI Generation Error:', error);
      throw new Error(handleGeminiError(error));
    }
  }

  /**
   * Stream text response chunk by chunk
   * @param {string} prompt - The user's input prompt
   * @param {string} userTier - User tier for model selection
   * @param {Function} onChunk - Callback to handle each streamed chunk
   * @param {AbortSignal} signal - Optional abort signal
   */
  static async streamText(prompt, userTier = 'free', onChunk, signal = null) {
    try {
      // OpenRouter supports streaming
      if (this.useOpenRouter()) {
        console.log('🌊 Streaming via OpenRouter...');
        return await openRouterClient.streamText(prompt, userTier, onChunk, {
          signal
        });
      }

      // Fallback to Gemini streaming
      console.log('🌊 Streaming via Gemini Direct...');
      const modelId = userTier === 'free' ? 'gemini-2.0-flash-exp' : 'gemini-2.0-flash-thinking-exp-1219';
      const model = genAI.getGenerativeModel({ 
        model: modelId,
        safetySettings: getSafetySettings()
      });

      const result = await model.generateContentStream({
        contents: [{ role: 'user', parts: [{ text: prompt }] }],
        generationConfig: {
          temperature: 0.7,
          topP: 0.8,
          topK: 40,
          maxOutputTokens: userTier === 'free' ? 1024 : 2048
        }
      });

      for await (const chunk of result.stream) {
        const text = chunk.text();
        if (text && onChunk) {
          onChunk(text);
        }
      }
    } catch (error) {
      console.error('❌ Streaming Error:', error);
      throw new Error(handleGeminiError(error));
    }
  }

  /**
   * Analyze resume - routes to OpenRouter or Gemini
   * @param {string} resumeText - Extracted resume text content
   * @param {string} userTier - User tier for model selection
   * @param {Array} preferredRoles - User's preferred job roles
   * @param {number} experienceYears - User's years of experience
   * @returns {Promise<Object>} Analysis results
   */
  static async analyzeResume(resumeText, userTier = 'free', preferredRoles = [], experienceYears = 0) {
    try {
      // Route 1: Use OpenRouter (Primary) - Has built-in validation
      if (this.useOpenRouter()) {
        console.log('🔍 Analyzing resume via OpenRouter...');
        return await openRouterClient.analyzeResume(resumeText, userTier, preferredRoles, experienceYears);
      }

      // Route 2: Fallback to Gemini Direct
      console.log('🔍 Analyzing resume via Gemini Direct...');
      const modelId = userTier === 'free' ? 'gemini-2.0-flash-exp' : 'gemini-2.0-flash-thinking-exp-1219';
      const model = genAI.getGenerativeModel({ 
        model: modelId,
        safetySettings: getSafetySettings()
      });

      const analysisPrompt = `Analyze this resume and provide detailed feedback in JSON format:

Resume Text:
${resumeText}

User Profile:
- Preferred Roles: ${preferredRoles.join(', ') || 'Not specified'}
- Experience Years: ${experienceYears || 'Not specified'}

Please analyze and return a JSON object with the following structure:
{
  "overall_score": number (0-100),
  "skills": [
    {
      "name": "skill name",
      "category": "technical/soft/domain",
      "proficiency": "beginner/intermediate/advanced/expert",
      "confidence_score": number (0-1),
      "years_experience": number,
      "is_technical": boolean
    }
  ],
  "feedback": [
    {
      "category": "format/content/skills/experience",
      "feedback_text": "detailed feedback",
      "priority_level": "low/medium/high",
      "improvement_suggestions": ["suggestion1", "suggestion2"]
    }
  ],
  "job_matches": [
    {
      "job_title": "title",
      "company_name": "company",
      "match_percentage": number (0-100),
      "match_range": "low/medium/high/excellent",
      "location": "India",
      "job_type": "full-time/part-time/contract",
      "experience_required": "X-Y years",
      "matching_skills": ["skill1", "skill2"],
      "missing_skills": ["skill1", "skill2"],
      "requirements": ["req1", "req2"],
      "salary_estimate": {"min": number, "max": number, "currency": "INR"}
    }
  ]
}

Focus on the Indian job market and use encouraging, constructive language.`;

      const result = await model.generateContent({
        contents: [{ role: 'user', parts: [{ text: analysisPrompt }] }],
        generationConfig: {
          temperature: 0.3, // Lower temperature for consistent structured output
          topP: 0.8,
          topK: 40,
          maxOutputTokens: userTier === 'free' ? 2048 : 4096
        }
      });
      
      const response = await result.response;
      const responseText = response.text();
      
      // Parse JSON response
      try {
        const jsonMatch = responseText.match(/\{[\s\S]*\}/);
        const jsonText = jsonMatch ? jsonMatch[0] : responseText;
        const analysis = JSON.parse(jsonText);
        
        // Use OpenRouter's validation (it's more robust)
        return openRouterClient.validateAnalysisResponse(analysis);
      } catch (parseError) {
        console.error('❌ Failed to parse Gemini response:', parseError);
        throw new Error('Failed to analyze resume. Please try again.');
      }
    } catch (error) {
      console.error('❌ Resume Analysis Error:', error);
      throw new Error('Failed to analyze resume. Please try again.');
    }
  }

  /**
   * Chat with history - routes to OpenRouter or Gemini
   * @param {string} prompt - The user's input prompt
   * @param {Array} history - The chat history
   * @param {string} userTier - User tier for model selection
   * @returns {Promise<{response: string, updatedHistory: Array}>}
   */
  static async chatWithHistory(prompt, history = [], userTier = 'free') {
    try {
      // Route 1: Use OpenRouter (Primary)
      if (this.useOpenRouter()) {
        console.log('💬 Chat via OpenRouter...');
        const messages = [
          ...history,
          { role: 'user', parts: [{ text: prompt }] }
        ];
        return await openRouterClient.chatWithHistory(messages, userTier);
      }

      // Route 2: Fallback to Gemini Direct
      console.log('💬 Chat via Gemini Direct...');
      const modelId = userTier === 'free' ? 'gemini-2.0-flash-exp' : 'gemini-2.0-flash-thinking-exp-1219';
      const model = genAI.getGenerativeModel({ 
        model: modelId,
        safetySettings: getSafetySettings()
      });
      
      const chat = model.startChat({ 
        history,
        generationConfig: {
          temperature: 0.7,
          topP: 0.8,
          topK: 40,
          maxOutputTokens: userTier === 'free' ? 1024 : 2048
        }
      });

      const result = await chat.sendMessage(prompt);
      const response = await result.response;
      const text = response.text();

      return {
        response: text,
        updatedHistory: [
          ...history,
          { role: 'user', parts: [{ text: prompt }] },
          { role: 'model', parts: [{ text }] }
        ]
      };
    } catch (error) {
      console.error('❌ Chat Error:', error);
      throw new Error(handleGeminiError(error));
    }
  }

  /**
   * Generate text from image (multimodal)
   * @param {string} prompt - The text prompt
   * @param {File} imageFile - The image file
   * @param {string} userTier - User tier for model selection
   * @returns {Promise<string>} The generated text
   */
  static async generateTextFromImage(prompt, imageFile, userTier = 'free') {
    try {
      // OpenRouter supports vision models
      if (this.useOpenRouter()) {
        console.log('👁️ Vision analysis via OpenRouter...');
        // OpenRouter can handle vision tasks with GPT-4V or Claude 3
        return await openRouterClient.generateText(
          `[Image Analysis] ${prompt}`,
          userTier
        );
      }

      // Fallback to Gemini vision
      console.log('👁️ Vision analysis via Gemini Direct...');
      const modelId = 'gemini-2.0-flash-exp'; // Flash supports vision
      const model = genAI.getGenerativeModel({ 
        model: modelId,
        safetySettings: getSafetySettings()
      });

      // Convert image to base64
      const imageBase64 = await this.fileToBase64(imageFile);
      const imagePart = {
        inlineData: {
          data: imageBase64,
          mimeType: imageFile.type,
        },
      };

      const result = await model.generateContent({
        contents: [{ 
          role: 'user', 
          parts: [imagePart, { text: prompt }] 
        }],
        generationConfig: {
          temperature: 0.7,
          maxOutputTokens: userTier === 'free' ? 1024 : 2048
        }
      });
      
      const response = await result.response;
      return response.text();
    } catch (error) {
      console.error('❌ Vision Error:', error);
      throw new Error(handleGeminiError(error));
    }
  }

  /**
   * Convert file to base64
   * @param {File} file - File to convert
   * @returns {Promise<string>} Base64 string
   */
  static fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = (error) => reject(error);
    });
  }
}

export default GeminiService;